import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AfricaMapComponent } from '../../africa-map/africa-map.component';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import {
  IgxCategoryChartModule,
  IgxPieChartModule,
  IgxItemLegendModule,
  IgxLegendModule,
  IgxDataPieChartComponent,
  IgxCategoryChartComponent,
  IgxDataLegendComponent
} from 'igniteui-angular-charts';
import { ReportsIntergrationService } from '../../services/reports-intergration.service';
import {
  CompletionTableData,
  MAndEData,
  MandEConfigModel
} from '../../models/studentProfileModels';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';
import { HttpHelperService } from '../../services/helper.service';
import { DateRangeDialogComponent } from '../../date-range-dialog/date-range-dialog.component';
import { ReportsBaseService } from '../../services/reports-base.service';
import { ChartJsComponent } from '../../shared/chart-js/chart-js.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TooltipService } from '../../services/tooltip.service';

type LwaziFeedbackPathwayId = 'business' | 'personal';

interface LwaziFeedbackInsightSection {
  label: string;
  pathway: LwaziFeedbackPathwayId;
}

interface LwaziFeedbackView {
  mAndEResponses: MAndEData[];
  mAndEConfig: MandEConfigModel[];
  mAndEChart: any;
}

@Component({
  selector: 'app-lwazi-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule,
    MatTableModule,
    IgxCategoryChartModule,
    IgxPieChartModule,
    IgxItemLegendModule,
    IgxLegendModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule,
    AfricaMapComponent,
    ChartJsComponent,
  ],
  templateUrl: './lwazi-report.component.html',
  styleUrls: ['./lwazi-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LwaziReportComponent implements OnInit {

  personalFinanceTableData: CompletionTableData[] = [];
  businessFinanceTableData: CompletionTableData[] = [];
  mAndEData: MAndEData[] = [];
  trend: { percentage: number; color: string } | null = null;

  readonly lwaziFeedbackInsightSections: LwaziFeedbackInsightSection[] = [
    { label: 'Business Finance', pathway: 'business' },
    { label: 'Personal Finance', pathway: 'personal' },
  ];

  countryPieChartOptions: any = {
    indexAxis: 'y',
    plugins: { legend: { display: false } }
  };

  prePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: { callbacks: { label: (context: any) => { const label = context.dataset.label || ''; const val = context.parsed.x; return `${label}: ${val}%`; } } }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: { ticks: { autoSkip: false, callback: (value: any, index: any) => {
        let configKey = '';
        if (this.baseService.selectedBot === 'lwazi') {
          configKey = 'lwazi_business';
        }
        const config = this.baseService.prePostQuizQuestionsConfig || {};
        const customLabels = config[configKey || ''] || [];
        const rawLabel = this.baseService.businessPrePostChartData?.labels?.[index] as string || '';
        return customLabels[index] || rawLabel || value;
      } } }
    }
  };

  personalPrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: { callbacks: { label: (context: any) => { const label = context.dataset.label || ''; const val = context.parsed.x; return `${label}: ${val}%`; } } }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: { ticks: { autoSkip: false, callback: (value: any, index: any) => {
        let configKey = '';
        if (this.baseService.selectedBot === 'lwazi') {
          configKey = 'lwazi_personal';
        }
        const config = this.baseService.prePostQuizQuestionsConfig || {};
        const customLabels = config[configKey || ''] || [];
        const rawLabel = this.baseService.personalPrePostChartData?.labels?.[index] as string || '';
        return customLabels[index] || rawLabel || value;
      } } }
    }
  };

  constructor(
    private reportsService: ReportsIntergrationService,
    private authService: KeycloakAuthService,
    private helperService: HttpHelperService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public baseService: ReportsBaseService,
    public tooltips: TooltipService,
  ) { }

  ngOnInit(): void {
    this.baseService.updateDateRange();
    this.checkUserBotAccess();
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      data: { startDate: this.baseService.startDate, endDate: this.baseService.endDate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.baseService.startDate = result.startDate;
        this.baseService.endDate = result.endDate;
        this.onDateChange();
      }
    });
  }

  getPercentage(count: number): string {
    const total = this.baseService.countryData.reduce((sum, country) => sum + country.count, 0);
    return total === 0 ? '0' : ((count / total) * 100).toFixed(2);
  }

  private checkUserBotAccess(): void {
    this.baseService.isLoading = true;
    this.baseService.error = null;

    const user = this.authService.currentUserValue();
    if (user && user.roles) {
      this.processUserBotAccess(user);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((u: any) => !!u && !!u.roles),
        take(1)
      )
      .subscribe({
        next: (u: any) => {
          this.processUserBotAccess(u);
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.baseService.error = "Failed to load user data. Please try logging in again.";
          this.baseService.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processUserBotAccess(user: any): void {
    const botRoles = user.roles.filter((role: string) => role.startsWith('bot_access_'));
    this.baseService.availableBots = botRoles.map((role: string) => {
      const botName = role.replace('bot_access_', '');
      return {
        key: botName as 'lwazi',
        name: botName.charAt(0).toUpperCase() + botName.slice(1)
      };
    });

    this.baseService.availableBots = this.baseService.availableBots.filter(bot => bot.key === 'lwazi');

    if (this.baseService.availableBots.length > 0) {
      this.baseService.selectedBot = 'lwazi';
      this.loadAllData();
    } else {
      this.baseService.error = "You do not have access to Lwazi bot. Please contact an administrator.";
    }
    this.baseService.isLoading = false;
    this.cdr.detectChanges();
  }

  onDateChange(): void {
    this.baseService.clearData();
    this.baseService.updateDateRange();
    if (this.baseService.selectedBot) {
      this.loadAllData();
    }
  }

  onProvinceFilterChange(): void {
    this.baseService.updateProvinceChartForFilter();
    this.cdr.detectChanges();
  }

  onLanguageFilterChange(): void {
    this.baseService.updateLanguageChartForFilter();
    this.cdr.detectChanges();
  }

  buildPrePostQuizChartData() {
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    const legendData = [
      { quiz: 'Pre-Learning', color: preColor },
      { quiz: 'Post-Learning', color: postColor }
    ];

    // 1. Build Personal Data
    this.baseService.personalPrePostChartData = this.generateChartConfig(
      this.baseService.personalCurrentPeriodStats
    );
    if (this.baseService.personalPrePostChartData) {
      this.baseService.personalPrePostQuizQuestionsIndexData = legendData;
    } else {
      this.baseService.personalPrePostQuizQuestionsIndexData = [];
    }

    // 2. Build Business Data
    this.baseService.businessPrePostChartData = this.generateChartConfig(
      this.baseService.businessCurrentPeriodStats
    );

    if (this.baseService.businessPrePostChartData) {
      this.baseService.businessPrePostQuizQuestionsIndexData = legendData;
    } else {
      this.baseService.businessPrePostQuizQuestionsIndexData = [];
    }
  }

  generateChartConfig(stats: any[]) {
    if (!stats || stats.length === 0) return null;

    const questions = stats.map(q => q.quizQuestion);
    const preData = stats.map(q => parseFloat(q.pre_learning_percentage) || 0);
    const postData = stats.map(q => parseFloat(q.post_learning_percentage) || 0);

    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';

    return {
      labels: questions,
      datasets: [
        {
          label: 'Pre-Learning',
          data: preData,
          backgroundColor: preColor,
          borderColor: preColor,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        },
        {
          label: 'Post-Learning',
          data: postData,
          backgroundColor: postColor,
          borderColor: postColor,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        }
      ]
    };
  }

  // Helper to reduce code duplication
  createDoughnutConfig(value: number, color: string, label: string) {
    return {
      labels: [label, 'Remaining'],
      datasets: [{
        data: [value, 100 - value],
        backgroundColor: [color, '#E0E0E0'],
        label: label
      }]
    };
  }

  buildAssessmentChartData(): void {
    // --- PERSONAL ---
    const personalPre =
      this.baseService.getAveragePreQuizScore(this.baseService.personalCurrentPeriodStats);
    const personalPost =
      this.baseService.getAveragePostQuizScore(this.baseService.personalCurrentPeriodStats);
    const personalDropOff = this.baseService.calculateDropOffStats('personal');
    const personalAvgEngagementScore = this.baseService.getOverallEngagementStats('personal');

    this.baseService.personalPreQuizChart =
      this.createDoughnutConfig(personalPre, this.baseService.pieChartColor['preQuiz'][0], 'Pre-Quiz');
    this.baseService.personalPostQuizChart =
      this.createDoughnutConfig(personalPost, this.baseService.pieChartColor['postQuiz'][0], 'Post-Quiz');
    this.baseService.personalEngagementChart =
      this.createDoughnutConfig(personalAvgEngagementScore, this.baseService.pieChartColor['overAllEngagement'][0], 'Engagement');
    this.baseService.personalDropOffChartData =
      this.createDoughnutConfig(personalDropOff, this.baseService.pieChartColor['overAllEngagement'][0], 'Engagement');

    // --- BUSINESS ---
    const businessPre = this.baseService.getAveragePreQuizScore(this.baseService.businessCurrentPeriodStats);
    const businessPost = this.baseService.getAveragePostQuizScore(this.baseService.businessCurrentPeriodStats);
    const businessDropOff = this.baseService.calculateDropOffStats('business');
    const businessAvgEngagementScore = this.baseService.getOverallEngagementStats('business');

    this.baseService.businessPreQuizChart =
      this.createDoughnutConfig(businessPre, this.baseService.pieChartColor['preQuiz'][0], 'Pre-Quiz');
    this.baseService.businessPostQuizChart =
      this.createDoughnutConfig(businessPost, this.baseService.pieChartColor['postQuiz'][0], 'Post-Quiz');
    this.baseService.businessEngagementChart =
      this.createDoughnutConfig(businessAvgEngagementScore, this.baseService.pieChartColor['overAllEngagement'][0], 'Engagement');

    this.baseService.businessDropOffChartData =
      this.createDoughnutConfig(businessDropOff, this.baseService.pieChartColor['overAllEngagement'][0], 'Drop-Off');
  }

  buildMAndEChartData(type: 'personal' | 'business') {
    const rawData =
      type === 'personal'
        ? this.baseService.personalMAndEAverages
        : this.baseService.businessMAndEAverages;

    const chartConfig = this.baseService.buildFeedbackRatingOverTimeChartData(rawData);

    if (type === 'personal') {
      this.baseService.personalMAndEChart = chartConfig;
    } else {
      this.baseService.businessMAndEChart = chartConfig;
    }
  }

  resolveLwaziFeedbackView(pathway: LwaziFeedbackPathwayId): LwaziFeedbackView {
    if (pathway === 'business') {
      return {
        mAndEResponses: this.baseService.businessMAndEResponses,
        mAndEConfig: this.baseService.businessMAndEConfigArray,
        mAndEChart: this.baseService.businessMAndEChart,
      };
    }
    return {
      mAndEResponses: this.baseService.personalMAndEResponses,
      mAndEConfig: this.baseService.personalMAndEConfigArray,
      mAndEChart: this.baseService.personalMAndEChart,
    };
  }

  onEmploymentDateChange(): void {
    if (this.baseService.selectedBot) {
      this.baseService.fetchEmploymentData(this.baseService.formatDate(this.baseService.employmentStartDate), this.baseService.formatDate(this.baseService.employmentEndDate));
    }
  }

  onRaceDateChange(): void {
    if (this.baseService.selectedBot) {
      this.baseService.fetchRaceData(this.baseService.formatDate(this.baseService.raceStartDate), this.baseService.formatDate(this.baseService.raceEndDate));
    }
  }

  onTabChange(event: any): void {
    const index = event === null ? 0 : event.index;

    if (index === 1) {
      this.buildMAndEChartData('business');
      this.cdr.detectChanges();
    }

    if (index === 2) {
      this.buildMAndEChartData('personal');
      this.cdr.detectChanges();
    }

    if (index === 3) {
      this.buildMAndEChartData('business');
      this.buildMAndEChartData('personal');
      this.cdr.detectChanges();
    }
  }

  private loadAllData(): Promise<void> {
    const localBot = this.baseService.selectedBot;
    if (!localBot) return Promise.resolve();

    this.baseService.isLoading = true;
    const currentStart = this.baseService.formatDate(this.baseService.startDate);
    const currentEnd = this.baseService.formatDate(this.baseService.endDate);
    const { prevStart, prevEnd } = this.baseService.getPreviousPeriod(this.baseService.startDate, this.baseService.endDate);

    const quizStats = this.baseService.loadPrePostQuizStats(localBot, currentStart, currentEnd);
    const engagement = this.baseService.loadEngagementCount(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const completion = this.baseService.loadCompletionStats(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const mAndEStats = this.baseService.loadMAndEStats(localBot, currentStart, currentEnd);
    const mAndENumber = this.baseService.loadMAndENumber(localBot, currentStart, currentEnd);

    return Promise.all([
      this.baseService.loadRemainingData(currentStart, currentEnd),
      this.baseService.loadBreakdowns(localBot, currentStart, currentEnd),
      this.baseService.loadTables(localBot, currentStart, currentEnd, prevStart, prevEnd),

      quizStats.then(() => {
        this.buildPrePostQuizChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([quizStats, engagement, completion]).then(() => {
        this.buildAssessmentChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([mAndEStats, mAndENumber]).then(() => {
        this.mAndEData = [
          ...(this.baseService.personalMAndEResponses || []),
          ...(this.baseService.businessMAndEResponses || [])
        ];
        this.buildMAndEChartData('personal');
        this.buildMAndEChartData('business');
        this.baseService.updateMAndEConfig();
        this.cdr.detectChanges();
      }),
    ])
      .then(() => {
        this.baseService.isLoading = false;
        this.cdr.detectChanges();
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error in loadAllData chain:', error);
        this.baseService.isLoading = false;
        this.baseService.error = 'Failed to load report data';
        this.cdr.detectChanges();
      });
  }

  // Lwazi-specific clear (if needed, but use base)
  private clearData(): void {
    this.baseService.clearData();
  }
}
